import React, { useEffect, useState } from 'react';
import {
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    CircularProgress
} from '@material-ui/core';
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles';
import { FormattedMessage } from 'react-intl';
import * as Utils from '../../commons/utils/Utils';
import { useIntl } from 'react-intl';
import swal from 'sweetalert'
import Security from '../../commons/security/Security.js';

const FIELDS = ['monto_general', 'monto_preventivo', 'monto_correctivo', 'monto_gestoria', 'monto_vencimientos'];

const PresupuestoAsignacion = ({ presupuesto, relacion, onAsignacionChange }) => {

    const intl = useIntl()
    const [asignaciones, setAsignaciones]   = useState([]);
    const [loading, setLoading]             = useState(true);
    const [editingAll, setEditingAll]       = useState(false);
    const [tempValues, setTempValues]       = useState({});
    const [inicioPeriodo, setInicioPeriodo] = useState(false);

    const tienePresupuesto = FIELDS.some(function(f) { return (parseFloat(presupuesto[f]) || 0) > 0; });
    const canEdit = relacion.editar && tienePresupuesto && (!inicioPeriodo || Security.hasPermission('PERIODO_PRESUPUESTARIO_MONTOS_EDITAR_INICIADO'));

    const getTempValue = (id, field) => {
        return (tempValues[id] && tempValues[id][field] !== undefined)
            ? parseFloat(tempValues[id][field]) || 0
            : 0;
    };

    const getDisplayValue = (asignacion, field) => {
        if (editingAll) return getTempValue(asignacion.id, field);
        return parseFloat(asignacion[field]) || 0;
    };

    const handleEditAll = () => {
        var initial = {};
        asignaciones.forEach(function(a) {
            initial[a.id] = {};
            FIELDS.forEach(function(f) { initial[a.id][f] = parseFloat(a[f]) || 0; });
        });
        setTempValues(initial);
        setEditingAll(true);
    };

    const cancelAll = () => {
        setTempValues({});
        setEditingAll(false);
    };

    const handleInputChange = (id, field, value) => {
        setTempValues(function(prev) {
            var updated = Object.assign({}, prev);
            updated[id] = Object.assign({}, prev[id]);
            updated[id][field] = parseFloat(value) || 0;
            return updated;
        });
    };

    const validateMontos = () => {
        var totals = {};
        FIELDS.forEach(function(f) { totals[f] = 0; });

        asignaciones.forEach(function(a) {
            FIELDS.forEach(function(f) {
                totals[f] += editingAll ? getTempValue(a.id, f) : (parseFloat(a[f]) || 0);
            });
        });

        return FIELDS.every(function(f) {
            return totals[f] <= (parseFloat(presupuesto[f]) || 0);
        });
    };

    const saveAll = () => {
        // Validación: negativos
        var hasNegative = asignaciones.some(function(a) {
            return FIELDS.some(function(f) { return getTempValue(a.id, f) < 0; });
        });
        if (hasNegative) {
            swal('Monto no válido', 'Los montos no pueden ser negativos.', 'warning');
            return;
        }

        // Validación: suma no supera presupuesto padre
        if (!validateMontos()) {
            swal(
                intl.formatMessage({ id: "PresupuestoAsignacion.alert.monto_superado.title", defaultMessage: "Monto superado" }),
                intl.formatMessage({ id: "PresupuestoAsignacion.alert.monto_superado.message", defaultMessage: "El valor ingresado excede el presupuesto disponible" }),
                "warning"
            );
            return;
        }

        // Detectar filas con cambios reales
        var changed = asignaciones.filter(function(a) {
            return FIELDS.some(function(f) {
                return getTempValue(a.id, f) !== (parseFloat(a[f]) || 0);
            });
        });

        if (changed.length === 0) {
            setEditingAll(false);
            return;
        }

        // Aplicar tempValues al estado local
        var updatedAsignaciones = asignaciones.map(function(a) {
            if (!tempValues[a.id]) return a;
            return Object.assign({}, a, tempValues[a.id]);
        });

        var newDistribuido = updatedAsignaciones.reduce(function(sum, a) {
            return sum + FIELDS.reduce(function(s, f) { return s + (parseFloat(a[f]) || 0); }, 0);
        }, 0);

        Promise.all(
            changed.map(function(a) {
                return Utils.sendData('/presupuestos/guardar-asignaciones', 'POST', {
                    body: {
                        id:                  a.id,
                        monto_general:       getTempValue(a.id, 'monto_general'),
                        monto_preventivo:    getTempValue(a.id, 'monto_preventivo'),
                        monto_correctivo:    getTempValue(a.id, 'monto_correctivo'),
                        monto_gestoria:      getTempValue(a.id, 'monto_gestoria'),
                        monto_vencimientos:  getTempValue(a.id, 'monto_vencimientos'),
                        periodo_id:          presupuesto.periodo.id,
                        presupuestable_type: relacion.presupuestable,
                        presupuestable_id:   relacion.id,
                    },
                });
            })
        ).then(function() {
            setAsignaciones(updatedAsignaciones);
            setTempValues({});
            setEditingAll(false);
            if (onAsignacionChange) onAsignacionChange(presupuesto.id, newDistribuido);
        }).catch(function(error) {
            console.error('Error al guardar asignaciones:', error);
            // Re-fetch to ensure local state matches server after partial failure
            Utils.getData('/presupuestos/subregiones-bases/' + relacion.id + '?periodoId=' + presupuesto.periodo.id + '&presupuestableType=' + relacion.presupuestable)
                .then(function(fresh) { setAsignaciones(fresh || []); })
                .catch(function() {});
            setTempValues({});
            setEditingAll(false);
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [hijosData, inicioPeriodoData] = await Promise.all([
                    Utils.getData(`/presupuestos/subregiones-bases/${relacion.id}?periodoId=${presupuesto.periodo.id}&presupuestableType=${relacion.presupuestable}`),
                    Utils.getData(`/presupuestos/config-periodo-iniciada`),
                ]);
                setAsignaciones(hijosData);
                setInicioPeriodo(inicioPeriodoData);
            } catch (error) {
                console.error('Error al obtener datos de asignación:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f9f9f9', width: '100%', overflowX: 'auto' }}>
            <div style={{ padding: '10px', backgroundColor: '#ffffff', border: '1px solid rgb(0, 191, 188)', borderRadius: '5px', width: '95%', marginTop: '10px', overflow: 'scroll', maxHeight: '300px' }}>
                <TableContainer>
                    <Table aria-label="clases table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>
                                    {relacion.modulo === 'regiones' ? (
                                        <FormattedMessage id="PresupuestoAsignacion.rennder.column_asignacion.SubRegiones" defaultMessage="SUBREGIONES" />
                                    ) : (
                                        <FormattedMessage id="PresupuestoAsignacion.rennder.column_asignacion.Vehiculos" defaultMessage="BASES" />
                                    )}
                                </StyledTableCell>
                                {presupuesto.monto_general > 0 && (
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="presupuestoGrid.presupuesto_table.column_general.label" defaultMessage="GENERAL" />
                                    </StyledTableCell>
                                )}
                                {presupuesto.monto_preventivo > 0 && (
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="presupuestoGrid.presupuesto_table.column_preventivo.label" defaultMessage="PREVENTIVO" />
                                    </StyledTableCell>
                                )}
                                {presupuesto.monto_correctivo > 0 && (
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="presupuestoGrid.presupuesto_table.column_correctivo.label" defaultMessage="CORRECTIVO" />
                                    </StyledTableCell>
                                )}
                                {presupuesto.monto_gestoria > 0 && (
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="presupuestoGrid.presupuesto.column_gestoria.label" defaultMessage="GESTORIA" />
                                    </StyledTableCell>
                                )}
                                {presupuesto.monto_vencimientos > 0 && (
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="presupuestoGrid.presupuesto.column_vencimientos.label" defaultMessage="VENCIMIENTO" />
                                    </StyledTableCell>
                                )}
                                {canEdit && (
                                    <StyledTableCell align="center">
                                        {editingAll ? (
                                            <>
                                                <button type="button" className="btn btn-sm btn-icon btn-dt-grid text-success" onClick={saveAll} style={{ width: '30px' }}>
                                                    <i className="fa fa-check fa-xs" />
                                                </button>
                                                <button type="button" className="btn btn-sm btn-icon btn-dt-grid text-danger" onClick={cancelAll} style={{ width: '30px' }}>
                                                    <i className="fa fa-times fa-xs" />
                                                </button>
                                            </>
                                        ) : (
                                            <button type="button" className="btn btn-sm btn-icon btn-dt-grid text-success" onClick={handleEditAll} style={{ width: '30px' }}>
                                                <i className="fa fa-pencil fa-xs" />
                                            </button>
                                        )}
                                    </StyledTableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <StyledTableCell colSpan={2} align="center">
                                        <CircularProgress />
                                    </StyledTableCell>
                                </TableRow>
                            ) : (
                                asignaciones.map(function(asignacion) {
                                    return (
                                        <TableRow key={asignacion.id}>
                                            <StyledTableCell>{asignacion.nombre}</StyledTableCell>
                                            {presupuesto.monto_general > 0 && (
                                                <StyledTableCell>
                                                    {editingAll ? (
                                                        <input type="number" className="form-control"
                                                            value={getDisplayValue(asignacion, 'monto_general') === 0 ? '' : getDisplayValue(asignacion, 'monto_general')}
                                                            onChange={function(e) { handleInputChange(asignacion.id, 'monto_general', e.target.value); }}
                                                            placeholder="0" />
                                                    ) : parseFloat(asignacion.monto_general) || 0}
                                                </StyledTableCell>
                                            )}
                                            {presupuesto.monto_preventivo > 0 && (
                                                <StyledTableCell>
                                                    {editingAll ? (
                                                        <input type="number" className="form-control"
                                                            value={getDisplayValue(asignacion, 'monto_preventivo') === 0 ? '' : getDisplayValue(asignacion, 'monto_preventivo')}
                                                            onChange={function(e) { handleInputChange(asignacion.id, 'monto_preventivo', e.target.value); }}
                                                            placeholder="0" />
                                                    ) : parseFloat(asignacion.monto_preventivo) || 0}
                                                </StyledTableCell>
                                            )}
                                            {presupuesto.monto_correctivo > 0 && (
                                                <StyledTableCell>
                                                    {editingAll ? (
                                                        <input type="number" className="form-control"
                                                            value={getDisplayValue(asignacion, 'monto_correctivo') === 0 ? '' : getDisplayValue(asignacion, 'monto_correctivo')}
                                                            onChange={function(e) { handleInputChange(asignacion.id, 'monto_correctivo', e.target.value); }}
                                                            placeholder="0" />
                                                    ) : parseFloat(asignacion.monto_correctivo) || 0}
                                                </StyledTableCell>
                                            )}
                                            {presupuesto.monto_gestoria > 0 && (
                                                <StyledTableCell>
                                                    {editingAll ? (
                                                        <input type="number" className="form-control"
                                                            value={getDisplayValue(asignacion, 'monto_gestoria') === 0 ? '' : getDisplayValue(asignacion, 'monto_gestoria')}
                                                            onChange={function(e) { handleInputChange(asignacion.id, 'monto_gestoria', e.target.value); }}
                                                            placeholder="0" />
                                                    ) : parseFloat(asignacion.monto_gestoria) || 0}
                                                </StyledTableCell>
                                            )}
                                            {presupuesto.monto_vencimientos > 0 && (
                                                <StyledTableCell>
                                                    {editingAll ? (
                                                        <input type="number" className="form-control"
                                                            value={getDisplayValue(asignacion, 'monto_vencimientos') === 0 ? '' : getDisplayValue(asignacion, 'monto_vencimientos')}
                                                            onChange={function(e) { handleInputChange(asignacion.id, 'monto_vencimientos', e.target.value); }}
                                                            placeholder="0" />
                                                    ) : parseFloat(asignacion.monto_vencimientos) || 0}
                                                </StyledTableCell>
                                            )}
                                            {canEdit && <StyledTableCell />}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
};

export default PresupuestoAsignacion;
